import React from 'react';

/**
 * Quantity Input Component with Plus/Minus Buttons
 * Option A: Flanking buttons with editable input field
 * Wrapped with React.memo to prevent unnecessary re-renders
 */
const QuantityInput = React.memo(function QuantityInput({ 
  value = 0, 
  onChange, 
  min = 0, 
  max = 999,
  disabled = false,
  className = '',
  inputClassName = '',
  buttonClassName = ''
}) {
  
  const handleIncrement = () => {
    if (value < max && !disabled) {
      onChange(value + 1);
    }
  };

  const handleDecrement = () => {
    if (value > min && !disabled) {
      onChange(value - 1);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    
    // Allow empty string for clearing
    if (newValue === '') {
      onChange(min);
      return;
    }
    
    // Parse and validate
    const parsed = parseInt(newValue, 10);
    if (!isNaN(parsed)) {
      // Clamp between min and max
      const clamped = Math.max(min, Math.min(max, parsed));
      onChange(clamped);
    }
  };

  const handleBlur = () => {
    // Ensure value is valid on blur
    if (value === '' || value === null || isNaN(value)) {
      onChange(min);
    }
  };

  const handleKeyDown = (e) => {
    // Allow arrow keys to work
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleIncrement();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleDecrement();
    }
  };

  return (
    <div className={`animated-border-element inline-flex items-center border border-gray-300 rounded-md ${className}`}>
      {/* Minus Button */}
      <button
        type="button"
        onClick={handleDecrement}
        disabled={disabled || value <= min}
        className={`px-3 py-1.5 text-gray-600 hover:bg-orange-50 hover:text-custom-orange transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent border-r border-gray-300 ${buttonClassName}`}
        aria-label="Decrease quantity"
      >
        <span className="text-lg font-bold">−</span>
      </button>

      {/* Input Field */}
      <input
        type="number"
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        min={min}
        max={max}
        className={`w-16 text-center border-0 focus:outline-none focus:ring-0 py-1.5 text-sm font-medium disabled:bg-gray-50 disabled:cursor-not-allowed ${inputClassName}`}
        aria-label="Quantity"
      />

      {/* Plus Button */}
      <button
        type="button"
        onClick={handleIncrement}
        disabled={disabled || value >= max}
        className={`px-3 py-1.5 text-gray-600 hover:bg-orange-50 hover:text-custom-orange transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent border-l border-gray-300 ${buttonClassName}`}
        aria-label="Increase quantity"
      >
        <span className="text-lg font-bold">+</span>
      </button>
    </div>
  );
});

export default QuantityInput;
